import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { DailyLogCard } from "@/components/DailyLogCard";
import { WeeklyView } from "@/components/WeeklyView";
import { MonthlyView } from "@/components/MonthlyView";
import { AnnualView } from "@/components/AnnualView";
import { api, formatApiError } from "@/lib/api";
import { estToday } from "@/lib/grades";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  UserPlus, Mail, Send, Trash2, CheckCircle2, Clock, ChevronLeft, Loader2, Pencil,
} from "lucide-react";

function PatientForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", reminders_enabled: true });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/patients", form);
      toast.success("Patient created");
      setForm({ name: "", email: "", password: "", reminders_enabled: true });
      setOpen(false);
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-xl" data-testid="admin-add-patient-button">
          <UserPlus className="h-4 w-4 mr-1.5" /> Add Patient
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-2xl">
        <DialogHeader><DialogTitle className="font-serif text-2xl">New patient</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Full name</Label>
            <Input data-testid="patient-name-input" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input type="email" data-testid="patient-email-input" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label>Temporary password</Label>
            <Input data-testid="patient-password-input" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-xl"
              placeholder="min 6 characters" />
          </div>
          <div className="flex items-center justify-between rounded-xl bg-secondary px-4 py-3">
            <div>
              <div className="text-sm font-medium">9pm reminder emails</div>
              <div className="text-xs text-muted-foreground">Email if no entry logged that day</div>
            </div>
            <Switch checked={form.reminders_enabled} data-testid="patient-reminders-switch"
              onCheckedChange={(v) => setForm({ ...form, reminders_enabled: v })} />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} className="rounded-xl" data-testid="patient-save-button">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create patient
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditPatientDialog({ patient, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: patient.name, password: "", reminders_enabled: patient.reminders_enabled });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const body = { name: form.name, reminders_enabled: form.reminders_enabled };
      if (form.password) body.password = form.password;
      await api.put(`/patients/${patient.id}`, body);
      toast.success("Patient updated");
      setOpen(false);
      onUpdated();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-full h-8 w-8" data-testid={`patient-edit-${patient.id}`}>
          <Pencil className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-2xl">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Edit {patient.name}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Full name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label>New password (optional)</Label>
            <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="rounded-xl" placeholder="leave blank to keep current" />
          </div>
          <div className="flex items-center justify-between rounded-xl bg-secondary px-4 py-3">
            <div className="text-sm font-medium">9pm reminder emails</div>
            <Switch checked={form.reminders_enabled}
              onCheckedChange={(v) => setForm({ ...form, reminders_enabled: v })} />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} className="rounded-xl">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminDashboard() {
  const [patients, setPatients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedTodayEntry, setSelectedTodayEntry] = useState(undefined);
  const today = estToday();

  const load = useCallback(() => {
    api.get("/patients").then((r) => setPatients(r.data)).catch(() => setPatients([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadSelectedToday = useCallback(() => {
    if (!selected) return;
    api.get("/entries", { params: { start: today, end: today, patient_id: selected.id } })
      .then((r) => setSelectedTodayEntry(r.data[0] || null))
      .catch(() => setSelectedTodayEntry(null));
  }, [selected, today]);

  useEffect(() => { loadSelectedToday(); }, [loadSelectedToday]);

  const deletePatient = async (p) => {
    if (!window.confirm(`Delete ${p.name} and all their entries?`)) return;
    try {
      await api.delete(`/patients/${p.id}`);
      toast.success("Patient deleted");
      if (selected?.id === p.id) setSelected(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const testEmail = async (p) => {
    try {
      await api.post(`/reminders/test/${p.id}`);
      toast.success(`Test email sent to ${p.email}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const sendAllNow = async () => {
    try {
      const { data } = await api.post("/reminders/send-now");
      toast.success(`Reminders sent: ${data.sent}, skipped: ${data.skipped}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (selected) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 space-y-10">
          <Button variant="ghost" className="rounded-full -ml-2" onClick={() => setSelected(null)}
            data-testid="back-to-roster">
            <ChevronLeft className="h-4 w-4 mr-1" /> Back to roster
          </Button>
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center font-serif text-2xl text-primary">
              {selected.name.charAt(0)}
            </div>
            <div>
              <h1 className="font-serif text-3xl">{selected.name}</h1>
              <p className="text-muted-foreground text-sm">{selected.email}</p>
            </div>
          </div>
          <Tabs defaultValue="today" className="w-full">
            <TabsList className="rounded-full bg-secondary p-1 mb-8">
              <TabsTrigger value="today" data-testid="view-tab-today" className="rounded-full px-5">Today</TabsTrigger>
              <TabsTrigger value="weekly" data-testid="view-tab-weekly" className="rounded-full px-5">Weekly</TabsTrigger>
              <TabsTrigger value="monthly" data-testid="view-tab-monthly" className="rounded-full px-5">Monthly</TabsTrigger>
              <TabsTrigger value="annual" data-testid="view-tab-annual" className="rounded-full px-5">Annual</TabsTrigger>
            </TabsList>
            <TabsContent value="today">
              {selectedTodayEntry !== undefined && (
                <DailyLogCard patientId={selected.id} patientName={selected.name}
                  existing={selectedTodayEntry} onSaved={() => { loadSelectedToday(); load(); }} />
              )}
            </TabsContent>
            <TabsContent value="weekly"><WeeklyView patientId={selected.id} /></TabsContent>
            <TabsContent value="monthly"><MonthlyView patientId={selected.id} /></TabsContent>
            <TabsContent value="annual"><AnnualView patientId={selected.id} /></TabsContent>
          </Tabs>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <h1 className="font-serif text-4xl sm:text-5xl">Patient roster</h1>
            <p className="text-muted-foreground mt-1">Manage patients, review progress and daily log status.</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="rounded-xl" onClick={sendAllNow} data-testid="send-reminders-now-button">
              <Send className="h-4 w-4 mr-1.5" /> Send reminders now
            </Button>
            <PatientForm onCreated={load} />
          </div>
        </div>

        {patients.length === 0 ? (
          <Card className="p-12 rounded-2xl border-dashed text-center">
            <p className="text-muted-foreground">No patients yet. Add your first patient to get started.</p>
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {patients.map((p) => (
              <Card key={p.id} data-testid={`patient-card-${p.id}`}
                className="p-5 rounded-2xl border-border/70 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <button className="flex items-center gap-3 text-left" onClick={() => setSelected(p)}
                    data-testid={`patient-open-${p.id}`}>
                    <div className="h-11 w-11 rounded-full bg-primary/10 flex items-center justify-center font-serif text-lg text-primary">
                      {p.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-medium leading-tight">{p.name}</div>
                      <div className="text-xs text-muted-foreground">{p.email}</div>
                    </div>
                  </button>
                  <div className="flex">
                    <EditPatientDialog patient={p} onUpdated={load} />
                    <Button variant="ghost" size="icon" className="rounded-full h-8 w-8 text-destructive"
                      onClick={() => deletePatient(p)} data-testid={`patient-delete-${p.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  {p.logged_today ? (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full"
                      data-testid={`patient-status-${p.id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5" /> Logged today
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full"
                      data-testid={`patient-status-${p.id}`}>
                      <Clock className="h-3.5 w-3.5" /> Pending
                    </span>
                  )}
                  <Button variant="ghost" size="sm" className="rounded-full text-xs" onClick={() => testEmail(p)}
                    data-testid={`patient-test-email-${p.id}`}>
                    <Mail className="h-3.5 w-3.5 mr-1" /> Test email
                  </Button>
                </div>
                {!p.reminders_enabled && (
                  <p className="text-[10px] text-muted-foreground mt-2">Reminders disabled</p>
                )}
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
